import React, { useEffect, useState } from 'react';
import { ErrorMessage, Loading, UserIcon, UsernameIcon } from '@/src/ui';
import { apiRequest } from '@/src/services';

interface UserProfileDTO {
    id: string;
    username: string;
    gradeShortName: string;
    name: string;
    districtName: string;
    schoolName: string;
    className: string;
    teacherNames: string[];
    isMiddleSchool: boolean;
}

interface UserProfileBarProps {
    headerBg?: string;
}

const UserProfileBar: React.FC<UserProfileBarProps> = ({ headerBg }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [userData, setUserData] = useState<UserProfileDTO | null>(null);

    useEffect(() => {
        (async function init() {
            setError('');
            setLoading(true);
            try {
                const res = await apiRequest<UserProfileDTO>('GET', 'dbo/users/userProfileInfo');
                setUserData(res.data);
            } catch {
                setError('Failed to fetch user data');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <Loading />;
    if (error) return <ErrorMessage error={error} />;

    return (
        <div className="flex justify-center w-full mb-6">
            {!userData ? (
                <ErrorMessage error={'No user data found.'} />
            ) : (
                <div
                    className="bg-gray-100 rounded-xl shadow-md p-6 w-full"
                    style={{ backgroundColor: headerBg, maxWidth: '900px' }}
                >
                    <div className="flex flex-col lg:flex-row items-center lg:items-start">
                        {/* Avatar Section - Fixed Width */}
                        <div className="flex-shrink-0 mr-0 md:mr-6 mb-4 md:mb-0">
                            <div className="p-2 bg-white rounded-full border-2 border-gray-200 shadow-sm">
                                <span role="img" aria-label="user">
                                    <UserIcon style={{ width: '40px', height: '40px' }} />
                                </span>
                            </div>
                        </div>

                        {/* Data Container */}
                        <div className="flex-grow flex flex-col gap-4 lg:gap-0 lg:flex-row items-center lg:items-stretch divide-y-2 lg:divide-y-0 lg:divide-x-2 divide-gray-200 w-full">
                            {/* Column 1: Identity (Name & Username) */}
                            {/* lg:flex-1 makes it equal width, min-w-0 allows text wrapping */}
                            <div className="px-2 flex flex-col justify-center space-y-2 w-full text-center items-center lg:px-4 lg:flex-1 lg:basis-0 lg:min-w-0 lg:text-left lg:items-start">
                                <div className="w-full">
                                    <h2
                                        className="text-xl font-bold leading-tight break-words"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        {userData?.name || 'N/A'}
                                    </h2>
                                </div>

                                <div className="flex items-center justify-center lg:justify-start gap-2 w-full">
                                    <UsernameIcon
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            color: 'var(--pm-primary)',
                                        }}
                                    />
                                    <span
                                        className="text-sm font-medium break-all"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        {userData?.username || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            {/* Column 2: Location (District & School) */}
                            <div className="px-2 flex flex-col justify-center space-y-2 w-full text-center items-center lg:px-4 lg:flex-1 lg:basis-0 lg:min-w-0 lg:text-left lg:items-start">
                                <div className="flex flex-col lg:flex-row items-center lg:items-start gap-1 lg:gap-2 w-full">
                                    <span
                                        className="text-sm font-bold uppercase lg:w-20 flex-shrink-0 text-center lg:text-left"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        District:
                                    </span>
                                    <span
                                        className="font-medium break-words text-sm w-full"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        {userData?.districtName || 'N/A'}
                                    </span>
                                </div>
                                <div className="flex flex-col lg:flex-row items-center lg:items-start gap-1 lg:gap-2 w-full">
                                    <span
                                        className="text-sm font-bold uppercase lg:w-20 flex-shrink-0 text-center lg:text-left"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        School:
                                    </span>
                                    <span
                                        className="font-medium break-words text-sm w-full"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        {userData?.schoolName || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            {/* Column 3: Details (Grade, Teacher, Class) */}
                            <div className="px-2 flex flex-col justify-center space-y-2 w-full text-center items-center lg:px-4 lg:flex-1 lg:basis-0 lg:min-w-0 lg:text-left lg:items-start">
                                <div className="flex flex-col lg:flex-row items-center lg:items-start gap-1 lg:gap-2 w-full">
                                    <span
                                        className="text-sm font-bold uppercase lg:w-20 flex-shrink-0 text-center lg:text-left"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        Grade:
                                    </span>
                                    <span
                                        className="font-medium break-words text-sm w-full"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        {userData?.gradeShortName || 'N/A'}
                                    </span>
                                </div>
                                <div className="flex flex-col lg:flex-row items-center lg:items-start gap-1 lg:gap-2 w-full">
                                    <span
                                        className="text-sm font-bold uppercase lg:w-20 flex-shrink-0 text-center lg:text-left"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        Teacher:
                                    </span>
                                    <span
                                        className="font-medium break-words text-sm w-full"
                                        style={{ color: 'var(--pm-primary)' }}
                                    >
                                        {userData?.teacherNames?.join(', ') || 'N/A'}
                                    </span>
                                </div>
                                {userData.isMiddleSchool && (
                                    <div className="flex flex-col lg:flex-row items-center lg:items-start gap-1 lg:gap-2 w-full">
                                        <span
                                            className="text-sm font-bold uppercase lg:w-20 flex-shrink-0 text-center lg:text-left"
                                            style={{ color: 'var(--pm-primary)' }}
                                        >
                                            Class:
                                        </span>
                                        <span
                                            className="font-medium break-words text-sm w-full"
                                            style={{ color: 'var(--pm-primary)' }}
                                        >
                                            {userData?.className || 'N/A'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfileBar;
